import { useState } from 'react';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleLineConsultation = () => {
    window.open('https://line.me/R/ti/p/@your-line-id', '_blank');
  };

  const handlePhoneCall = () => {
    window.location.href = 'tel:098-983-8751';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-white via-white to-green-50/30">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-green-100/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        
        <div className="container mx-auto px-6 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-6 py-2 bg-green-50 rounded-full border border-green-200">
                <span className="text-green-700 font-medium text-sm">沖縄市専門</span>
                <span className="text-green-400">|</span>
                <span className="text-green-700 font-medium text-sm">相続×不動産</span>
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold leading-tight text-gray-900">
                相続で受け継いだ土地…<br />
                <span className="text-green-600">「売るべきか、残すべきか」</span><br />
                どう考えればいいのか
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed">
                相続の整理、不動産の価値判断、活用・売却…<br />
                「何から始めたらいい」か分からない方へ。<br />
                沖縄市の相続×不動産に精通した私たちが、<br />
                お客様の状況に合わせて丁寧に対応いたします。<br />
                LINEでお気軽に相談できます、資料がなくても大丈夫です。
              </p>

              <div className="space-y-4">
                <button
                  onClick={handleLineConsultation}
                  className="group w-full lg:w-auto inline-flex items-center justify-center gap-4 px-10 py-5 bg-gradient-to-r from-green-500 to-green-600 text-white text-lg font-bold rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-line-fill text-3xl"></i>
                  <span>LINEで無料相談する</span>
                  <i className="ri-arrow-right-line text-2xl group-hover:translate-x-1 transition-transform"></i>
                </button>
                <p className="text-sm text-gray-500 text-center lg:text-left">
                  最初のメッセージは「困っています」だけでOK
                </p>
              </div>
            </div>

            {/* Right Illustration */}
            <div className="relative">
              <div className="absolute inset-0 bg-green-100/30 rounded-full blur-3xl"></div>
              <div className="relative">
                <img
                  src="https://public.readdy.ai/ai/img_res/2afb53a0df13b5322fb783ddb1cae5e4.jpg"
                  alt="相続と不動産のイメージ"
                  className="w-full h-auto rounded-3xl"
                />
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-green-200/50 rounded-full"></div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-green-100/50 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LINE Consultation Benefits */}
      <section id="line-benefits" className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              相続 × 不動産の悩みは、<span className="text-green-600">LINE</span>でお気軽にご相談いただけます
            </h2>
            <div className="w-32 h-1 bg-green-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {[
              { icon: 'ri-phone-line', title: '電話・来店不要', desc: 'ご自宅から気軽に相談できます' },
              { icon: 'ri-time-line', title: '24時間相談OK', desc: '深夜でも早朝でもメッセージ送信可能' },
              { icon: 'ri-robot-2-line', title: 'AI初期対応', desc: 'すぐに返信、必要時はスタッフが対応' },
              { icon: 'ri-camera-line', title: '写真送信だけでOK', desc: '土地の写真や書類を送るだけで状況整理' },
              { icon: 'ri-lock-line', title: '周囲に知られない', desc: 'プライバシーを守って相談できます' },
              { icon: 'ri-heart-line', title: '短い相談でも安心', desc: '「困っています」だけでも大丈夫' }
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-4xl text-green-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{item.title}</h3>
                <p className="text-gray-600 text-center leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={handleLineConsultation}
              className="inline-flex items-center gap-3 px-10 py-4 bg-green-600 text-white text-lg font-bold rounded-full hover:bg-green-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-line-fill text-2xl"></i>
              <span>悩みを送ってみる</span>
            </button>
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section id="problems" className="py-20 bg-gradient-to-br from-green-50/30 to-white">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Illustration */}
            <div className="space-y-6">
              <img
                src="https://readdy.ai/api/search-image?query=Gentle%20illustration%20of%20a%20person%20looking%20at%20inheritance%20documents%20and%20property%20papers%20with%20a%20thoughtful%20expression%2C%20warm%20and%20empathetic%20atmosphere%2C%20soft%20pastel%20colors%20with%20white%20and%20light%20green%20background%2C%20minimalist%20Japanese%20style%2C%20comforting%20and%20supportive%20mood%2C%20hand-drawn%20artistic%20approach&width=500&height=500&seq=problems-illustration&orientation=squarish"
                alt="お悩みのイメージ"
                className="w-full h-auto rounded-3xl"
              />
              <div className="bg-green-50 rounded-2xl p-8 border border-green-200">
                <p className="text-lg font-bold text-gray-900 mb-3">
                  あなたが悪いわけではありません。
                </p>
                <p className="text-gray-700 leading-relaxed">
                  相続×不動産の悩みは「正しい答えが一つではない」からこそ難しいのです。
                </p>
              </div>
            </div>

            {/* Right - Problems List */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-10">
                こんな悩み、ありませんか？
              </h2>
              <div className="space-y-4">
                {[
                  '相続した土地の扱いがわからない',
                  '名義が古い、誰の土地かわからない',
                  '売るべきか・残すべきか判断できない',
                  '家族間で話が進まない',
                  '固定資産税だけ毎年負担になっている',
                  '土地を放置して気になっている',
                  '県外に住んでいて動くのが難しい',
                  '接道や地形が問題と言われて不安',
                  '感情的に苦しく、誰に相談すべきかわからない'
                ].map((problem, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 bg-white rounded-xl p-5 border border-green-100 hover:bg-green-50/50 transition-all duration-300"
                  >
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                    </div>
                    <p className="text-gray-800 text-lg leading-relaxed">{problem}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <button
                  onClick={handleLineConsultation}
                  className="inline-flex items-center gap-3 px-8 py-4 border-2 border-green-600 text-green-600 font-bold rounded-full hover:bg-green-600 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  まずは相談してみる
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nroom Strengths */}
      <section id="strengths" className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              <span className="text-green-600">Nroom</span>が相続 × 不動産に強い理由
            </h2>
            <p className="text-xl text-gray-600">売却に偏らない、総合アドバイザーとして</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-10">
            {[
              {
                title: '相続×不動産を一体として整理',
                image: 'Illustration of inheritance documents and real estate papers being organized together on a desk, clean and professional style, white and light green color scheme, minimalist Japanese design, showing integration and comprehensive approach, soft lighting&width=400&height=300',
                points: [
                  '相続だけでも、不動産だけでも見ない',
                  '家族の状況、土地の特徴、将来の展望までサポート',
                  '売却に偏らない「総合アドバイザー」'
                ]
              },
              {
                title: '沖縄市の市場・文化・土地事情に精通',
                image: 'Stylized map illustration of Okinawa City with landmarks and neighborhoods, warm and friendly style, green and white color palette, showing local expertise and community knowledge, minimalist Japanese aesthetic, soft pastel tones&width=400&height=300',
                points: [
                  '接道問題、共有地、地籍の特殊性など地域のリアルを理解',
                  '地域の買主・需要・活用事例も把握',
                  '「地域だからこそできる提案」が強み'
                ]
              },
              {
                title: 'LINE×AI×人の三位一体サポート',
                image: 'Modern illustration of LINE chat interface with AI and human support icons, friendly and approachable design, green and white colors, showing seamless communication and support system, clean minimalist style&width=400&height=300',
                points: [
                  '気軽さ（LINE）',
                  '速さ（AI）',
                  '寄り添い（人）',
                  '相続×不動産に不安な人でも「相談しやすい窓口」'
                ]
              }
            ].map((strength, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
              >
                <div className="h-48 bg-green-50 overflow-hidden">
                  <img
                    src={`https://readdy.ai/api/search-image?query=${strength.image}&seq=strength-${index}&orientation=landscape`}
                    alt={strength.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">{strength.title}</h3>
                  <ul className="space-y-3">
                    {strength.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700 leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={handleLineConsultation}
              className="inline-flex items-center gap-3 px-10 py-4 bg-green-600 text-white text-lg font-bold rounded-full hover:bg-green-700 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-line-fill text-2xl"></i>
              <span>詳しく相談する</span>
            </button>
          </div>
        </div>
      </section>

      {/* Three Choices */}
      <section id="choices" className="py-20 bg-gradient-to-br from-green-50/40 to-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              答えは<span className="text-green-600">「売却」</span>だけではありません
            </h2>
            <p className="text-xl text-gray-600">
              あなたの状況に合わせた、最適な選択肢を一緒に見つけます
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            {[
              {
                color: 'blue',
                icon: 'ri-home-heart-line',
                title: '売らないという選択',
                points: [
                  '将来性がある',
                  '家族の想いを大切にしたい',
                  '残すことで安心が生まれる'
                ]
              },
              {
                color: 'orange',
                icon: 'ri-settings-3-line',
                title: '活用という選択',
                points: [
                  '駐車場・貸地として活用',
                  '畑・太陽光（エリアに応じて）',
                  '小規模でも収益化できることがある'
                ]
              },
              {
                color: 'green',
                icon: 'ri-hand-heart-line',
                title: '売却という選択',
                points: [
                  '「本当に売るべき時」だけ',
                  '沖縄市市場を踏まえた適正価格',
                  '地域特化だからスムーズな取引'
                ]
              }
            ].map((choice, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
              >
                <div className={`h-2 bg-${choice.color}-400`}></div>
                <div className="p-10">
                  <div className={`w-20 h-20 bg-${choice.color}-50 rounded-full flex items-center justify-center mx-auto mb-6`}>
                    <i className={`${choice.icon} text-5xl text-${choice.color}-600`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">{choice.title}</h3>
                  <ul className="space-y-4">
                    {choice.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <i className="ri-check-line text-xl text-green-600 mt-1"></i>
                        <span className="text-gray-700 leading-relaxed text-lg">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-green-50 rounded-2xl p-8 border border-green-200 text-center">
            <p className="text-xl font-bold text-gray-900 mb-2">
              どの選択が正しいかは「人によって違う」
            </p>
            <p className="text-lg text-gray-700">
              だから一緒に最適な選択肢を探します
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section id="cases" className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              沖縄市での実績
            </h2>
            <p className="text-xl text-gray-600">
              相続×不動産の成功事例
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {[
              {
                label: '解決事例',
                title: '接道なしの土地',
                flow: ['相続発生', '接道問題発覚', '整理・調整', '適正価格で売却'],
                result: '接道問題を解決し、安心して高値売却を実現'
              },
              {
                label: '解決事例',
                title: '相続人多数のケース',
                flow: ['相続人10名以上', '意見がバラバラ', '丁寧な調整', '円満解決'],
                result: '全員が納得する形で相続整理が完了'
              },
              {
                label: '解決事例',
                title: '県外在住の相続',
                flow: ['県外在住', 'LINE相談', '最適提案', '売却 or 保有選択'],
                result: '来沖不要でスムーズに手続き完了'
              },
              {
                label: '解決事例',
                title: '放置土地の整理',
                flow: ['長年放置', '相続整理のみ', '保有継続提案', '安心'],
                result: '売らずに保有する選択で心の負担が軽減'
              }
            ].map((caseItem, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border-2 border-green-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="inline-block px-4 py-1 bg-green-50 text-green-700 text-sm font-bold rounded-lg mb-4">
                  {caseItem.label}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{caseItem.title}</h3>
                <div className="flex items-center gap-3 mb-6 flex-wrap">
                  {caseItem.flow.map((step, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="px-4 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium whitespace-nowrap">
                        {step}
                      </div>
                      {idx < caseItem.flow.length - 1 && (
                        <i className="ri-arrow-right-line text-green-600 text-xl"></i>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">{caseItem.result}</p>
                <div className="flex items-center gap-2 text-green-600">
                  <i className="ri-check-double-line text-xl"></i>
                  <span className="font-bold">円満解決</span>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-green-50 rounded-2xl p-8 border-l-4 border-green-600 relative">
            <i className="ri-double-quotes-l text-6xl text-green-200 absolute top-4 left-4"></i>
            <p className="text-lg text-gray-800 leading-relaxed pl-12">
              「売らない選択肢」をご提案することも多く、相談者の方から「背中を押してもらって安心しました」というお声をいただいております。
            </p>
          </div>
        </div>
      </section>

      {/* Market Info */}
      <section id="market" className="py-20 bg-gradient-to-br from-green-50/30 to-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              沖縄市の市場を知る
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              沖縄市はエリアによって土地の価値・需要が大きく異なります。<br />
              売った方が良いケース、残すべきケース、その判断も土地ごとに違います。
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-10 text-center">
              2025年 沖縄市 坪単価目安
            </h3>
            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <div className="bg-green-50 rounded-2xl p-8 text-center">
                <p className="text-lg text-gray-700 font-bold mb-4">中心部</p>
                <p className="text-5xl font-bold text-green-600 mb-2">35〜40万円</p>
                <p className="text-sm text-gray-600">/ 坪</p>
              </div>
              <div className="bg-green-50 rounded-2xl p-8 text-center">
                <p className="text-lg text-gray-700 font-bold mb-4">住宅地</p>
                <p className="text-5xl font-bold text-green-600 mb-2">30〜35万円</p>
                <p className="text-sm text-gray-600">/ 坪</p>
              </div>
            </div>
            <p className="text-center text-gray-700 leading-relaxed text-lg">
              ※ エリア・接道・形状により変動します
            </p>
          </div>

          <div className="text-center mt-12">
            <p className="text-2xl font-bold text-gray-900">
              市場を踏まえ、あなたの土地はどうあるべきかを一緒に整理します
            </p>
          </div>
        </div>
      </section>

      {/* LINE CTA Strong */}
      <section id="line-cta" className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-[3rem] p-16 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            
            <div className="relative z-10 text-center">
              <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-8">
                <i className="ri-line-fill text-6xl text-green-600"></i>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold mb-12">
                LINEなら、悩みをそのまま送るだけで相談できます
              </h2>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {[
                  { icon: 'ri-phone-line', text: '電話が苦手でも気軽に' },
                  { icon: 'ri-moon-line', text: '夜でもメッセージOK' },
                  { icon: 'ri-robot-line', text: 'AIがとりあえず対応' },
                  { icon: 'ri-image-line', text: '写真で状況整理が進む' },
                  { icon: 'ri-chat-smile-3-line', text: '文章が苦手でも大丈夫' },
                  { icon: 'ri-shield-check-line', text: '無理な提案は一切なし' }
                ].map((item, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                    <i className={`${item.icon} text-5xl mb-3 block`}></i>
                    <p className="text-lg font-bold">{item.text}</p>
                  </div>
                ))}
              </div>

              <button
                onClick={handleLineConsultation}
                className="inline-flex items-center gap-4 px-14 py-6 bg-white text-green-600 text-2xl font-bold rounded-full shadow-2xl hover:-translate-y-2 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-line-fill text-4xl"></i>
                <span>LINEで無料相談する</span>
                <i className="ri-arrow-right-line text-3xl"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Steps */}
      <section id="steps" className="py-20 bg-gradient-to-br from-green-50/20 to-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              3ステップで不安が軽くなる
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-12 items-start mb-12">
            {[
              {
                num: '01',
                icon: 'ri-chat-3-line',
                title: 'LINEで状況や気持ちを送る',
                desc: '「困っています」だけでもOK。まずはお気軽にメッセージを'
              },
              {
                num: '02',
                icon: 'ri-search-eye-line',
                title: '相続×不動産の視点から整理',
                desc: '専門家が状況を整理し、選択肢を明確にします'
              },
              {
                num: '03',
                icon: 'ri-lightbulb-line',
                title: '最適解をご提案',
                desc: '売る・残す・活用の中から、あなたに合った答えを'
              }
            ].map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-full w-72 h-72 mx-auto shadow-lg flex flex-col items-center justify-center p-8">
                  <p className="text-6xl font-bold text-green-600 mb-4">{step.num}</p>
                  <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-4">
                    <i className={`${step.icon} text-4xl text-green-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{step.title}</h3>
                  <p className="text-gray-600 text-center leading-relaxed text-sm">{step.desc}</p>
                </div>
                {index < 2 && (
                  <div className="hidden lg:block absolute top-1/2 -right-6 transform -translate-y-1/2">
                    <i className="ri-arrow-right-line text-4xl text-green-300"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">
              あなたのペースで大丈夫です
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              お客様の声
            </h2>
            <p className="text-xl text-gray-600">
              実際にご相談いただいた方々からのメッセージ
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                stars: 5,
                text: '売却を勧められると思っていたが、「売らない選択」も提案していただいて安心しました。家族の想いを大切にできて本当に良かったです。',
                name: 'K.T様',
                attr: '50代・沖縄市在住'
              },
              {
                stars: 5,
                text: '相続の不安が整理されて気持ちが軽くなりました。何から始めればいいか分からなかったのですが、丁寧に教えていただけました。',
                name: 'M.Y様',
                attr: '60代・県外在住'
              },
              {
                stars: 5,
                text: 'LINEで相談できたのが本当に助かりました。夜中でもメッセージを送れて、翌朝には返信が来ていて安心できました。',
                name: 'S.N様',
                attr: '40代・沖縄市在住'
              },
              {
                stars: 5,
                text: '接道問題があって諦めていましたが、解決策を提案していただき、納得のいく価格で売却できました。地域に詳しいからこその提案でした。',
                name: 'H.K様',
                attr: '70代・沖縄市在住'
              },
              {
                stars: 5,
                text: '相続人が多くて調整が大変でしたが、全員が納得する形でまとめていただきました。プロの力を借りて本当に良かったです。',
                name: 'T.I様',
                attr: '50代・沖縄市在住'
              },
              {
                stars: 5,
                text: '土地を活用する提案をいただき、駐車場として収益化できました。売却だけが答えじゃないと気づかせてもらいました。',
                name: 'R.O様',
                attr: '60代・沖縄市在住'
              }
            ].map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.stars)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-2xl text-green-500"></i>
                  ))}
                </div>
                <i className="ri-double-quotes-l text-4xl text-green-200 block mb-3"></i>
                <p className="text-gray-800 leading-relaxed mb-6 text-lg">
                  {testimonial.text}
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-green-700 font-bold text-lg">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.attr}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="final-cta" className="py-24 bg-gradient-to-br from-green-600 to-green-700 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="container mx-auto px-6 max-w-4xl text-center relative z-10">
          <div className="inline-block px-6 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white font-bold mb-8">
            無料相談受付中
          </div>
          
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8 leading-tight">
            あなたにとって一番良い答えを、<br />一緒に見つけましょう
          </h2>
          
          <p className="text-xl text-white/90 mb-12">
            相続×不動産の悩みは、一人で抱え込まないでください
          </p>

          <button
            onClick={handleLineConsultation}
            className="inline-flex items-center gap-4 px-16 py-7 bg-white text-green-600 text-3xl font-bold rounded-full shadow-2xl hover:-translate-y-2 hover:scale-105 transition-all duration-300 mb-8 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-line-fill text-5xl"></i>
            <span>LINEで無料相談する</span>
            <i className="ri-arrow-right-line text-4xl"></i>
          </button>

          <div className="flex items-center justify-center gap-3 mb-4">
            <i className="ri-phone-fill text-3xl text-white"></i>
            <a
              href="tel:098-983-8751"
              className="text-3xl font-bold text-white hover:text-green-100 transition-colors cursor-pointer"
            >
              098-983-8751
            </a>
          </div>
          <p className="text-white/80">
            お電話でのご相談も承っております
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-green-900 text-white py-16">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* Company Info */}
            <div>
              <h3 className="text-3xl font-bold mb-6">Nroom</h3>
              <p className="text-white/70 leading-relaxed mb-6">
                沖縄市専門の相続×不動産総合アドバイザー。<br />
                お客様の人生・家族・将来に寄り添った最適提案を行います。
              </p>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                  <i className="ri-line-fill text-xl"></i>
                </a>
              </div>
            </div>

            {/* Menu */}
            <div>
              <h4 className="text-xl font-bold mb-6">メニュー</h4>
              <ul className="space-y-3">
                {[
                  { text: 'LINE相談の強み', id: 'line-benefits' },
                  { text: 'Nroomの強み', id: 'strengths' },
                  { text: '実績紹介', id: 'cases' },
                  { text: 'お客様の声', id: 'testimonials' }
                ].map((item, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className="text-white/80 hover:text-white hover:underline transition-all cursor-pointer"
                    >
                      {item.text}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-xl font-bold mb-6">お問い合わせ</h4>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <i className="ri-phone-fill text-2xl"></i>
                  <a href="tel:098-983-8751" className="text-xl font-bold hover:text-green-200 transition-colors cursor-pointer">
                    098-983-8751
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-mail-fill text-xl"></i>
                  <a href="mailto:info@nroom-okinawa.com" className="text-white/80 hover:text-white transition-colors cursor-pointer">
                    info@nroom-okinawa.com
                  </a>
                </div>
                <div className="mt-6">
                  <h5 className="text-lg font-bold mb-3">所在地</h5>
                  <p className="text-white/80 leading-relaxed">
                    〒904-0000<br />
                    沖縄県沖縄市○○○○
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/60 text-sm">
                © 2025 Nroom. All rights reserved.
              </p>
              <div className="flex gap-6 text-sm">
                <a href="#" className="text-white/60 hover:text-white transition-colors cursor-pointer">
                  プライバシーポリシー
                </a>
                <span className="text-white/40">|</span>
                <a href="#" className="text-white/60 hover:text-white transition-colors cursor-pointer">
                  利用規約
                </a>
                <span className="text-white/40">|</span>
                <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors cursor-pointer">
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
